import { Eye, EyeClosed } from 'iconoir-react';
import React, { useId, useRef, useState, type InputHTMLAttributes } from 'react';

type InputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'prefix' | 'suffix'
> & {
  size?: 'xl' | 'lg' | 'md';
  variant?: 'default' | 'outline';
  color?: 'default' | 'success' | 'warning' | 'error' | 'info';
  wrapperClassName?: string;
  label?: string;
  labelClassName?: string;
  helperText?: string;
  helperTextClassName?: string;
  inputClassName?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  prefixClassName?: string;
  suffixClassName?: string;
};

const Input = (props: InputProps) => {
  const {
    size = 'lg',
    variant = 'default',
    color = 'default',
    label,
    labelClassName = '',
    helperText,
    helperTextClassName = '',
    className = '',
    wrapperClassName = '',
    inputClassName = '',
    prefix,
    suffix,
    prefixClassName = '',
    suffixClassName = '',
    disabled = false,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [internalType, setInternalType] = useState(rest.type);

  const colorStyles = {
    default: {
      default:
        'border-2 border-gray-200 hover:border-purple-500 focus-within:!border-purple-500 focus-within:[&+p]:!text-purple-500 focus-within:[&>span]:!text-purple-500',
      success:
        'border-2 border-green-200 hover:border-green-400 [&+p]:!text-green-500 [&>span]:!text-green-500 focus-within:!border-purple-500 focus-within:[&+p]:!text-purple-500 focus-within:[&>span]:!text-purple-500',
      warning:
        'border-2 border-yellow-200 hover:border-yellow-400 [&+p]:!text-yellow-500 [&>span]:!text-yellow-500 focus-within:!border-purple-500 focus-within:[&+p]:!text-purple-500 focus-within:[&>span]:!text-purple-500',
      error:
        'border-2 border-red-700 hover:border-red-700 [&+p]:!text-red-700 [&>span]:!text-red-700 focus-within:!border-red-700 focus-within:[&+p]:!text-red-700 focus-within:[&>span]:!text-red-700',
      info: 'border-2 border-blue-200 hover:border-blue-400 [&+p]:!text-blue-500 [&>span]:!text-blue-500 focus-within:!border-purple-500 focus-within:[&+p]:!text-purple-500 focus-within:[&>span]:!text-purple-500',
    },
    outline: {
      default:
        'bg-transparent autofill:bg-transparent border-2 border-gray-200 hover:border-gray-400 focus-within:!border-primary-500 focus-within:[&+p]:!text-primary-500 focus-within:[&>span]:!text-primary-500',
      success:
        'bg-transparent autofill:bg-transparent border-2 border-green-200 hover:border-green-400 [&+p]:!text-green-500 [&>span]:!text-green-500 focus-within:!border-primary-500 focus-within:[&+p]:!text-primary-500 focus-within:[&>span]:!text-primary-500',
      warning:
        'bg-transparent autofill:bg-transparent border-2 border-yellow-200 hover:border-yellow-400 [&+p]:!text-yellow-500 [&>span]:!text-yellow-500 focus-within:!border-primary-500 focus-within:[&+p]:!text-primary-500 focus-within:[&>span]:!text-primary-500',
      error:
        'bg-transparent border-2 border-red-200 hover:border-red-400 [&+p]:!text-red-500 [&>span]:!text-red-500 focus-within:!border-red-500 focus-within:[&+p]:!text-red-500 focus-within:[&>span]:!text-red-500',
      info: 'bg-transparent border-2 border-blue-200 hover:border-blue-400 [&+p]:!text-blue-500 [&>span]:!text-blue-500 focus-within:!border-primary-500 focus-within:[&+p]:!text-primary-500 focus-within:[&>span]:!text-primary-500',
    },
  };

  const sizeClasses = {
    md: 'px-3 h-10 text-[0.875rem] leading-[1.25rem] rounded-xl',
    lg: 'px-3 h-12 text-[1rem] leading-[1.25rem] rounded-xl',
    xl: 'px-3 h-15 text-[1rem] leading-[1.5rem] rounded-xl',
  };

  const disabledClasses =
    'cursor-not-allowed pointer-events-none bg-gray-50 border-gray-200 text-gray-400';
  const baseWrapperClassName =
    'cursor-default duration-300 flex items-center justify-center gap-2';
  const baseInputClassName =
    'bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent border-none outline-none flex-grow h-full w-full';
  const baseLabelClassName = 'text-sm font-medium' + ' ' + labelClassName;
  const baseHelperTextClassName =
    'text-sm text-gray-400 mb-0' + ' ' + helperTextClassName;
  const basePrefixClassName = `[&>svg]:w-6 [&>svg]:h-6 text-gray-400 ${prefixClassName}`;
  const baseSuffixClassName = `[&>svg]:w-6 [&>svg]:h-6 text-gray-400 ${suffixClassName}`;
  // The label rendered below pointed `htmlFor` at `rest.id`, but no call site
  // passes an id, so it resolved to undefined and the label was associated with
  // nothing. Measured on /settings: four inputs (firstName, lastName, email and
  // the avatar file input) announced as unlabelled despite showing visible
  // label text. Falling back to a generated id fixes every form that uses this
  // component, and an explicit id still wins where one is supplied.
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;

  const computedInputClassName = `${baseInputClassName} ${inputClassName}`;
  const computedClassName = `${baseWrapperClassName} ${colorStyles[variant][color]} ${sizeClasses[size]} ${className} ${disabled ? disabledClasses : ''}`;

  return (
    <div className={`flex flex-col gap-2 ${wrapperClassName}`}>
      {label && (
        <label htmlFor={inputId} className={baseLabelClassName}>
          {label}
        </label>
      )}
      <div
        className={computedClassName}
        onClick={() => inputRef.current?.showPicker()}
      >
        {prefix && <span className={basePrefixClassName}>{prefix}</span>}
        <input
          ref={inputRef}
          {...rest}
          id={inputId}
          type={internalType}
          className={computedInputClassName}
        />
        {rest.type === 'password' && (
          <span
            className={baseSuffixClassName}
            onClick={() =>
              setInternalType(internalType === 'password' ? 'text' : 'password')
            }
          >
            {internalType === 'password' ? (
              <EyeClosed className='cursor-pointer' />
            ) : (
              <Eye className='cursor-pointer' />
            )}
          </span>
        )}
        {suffix && rest.type !== 'password' && (
          <span className={baseSuffixClassName}>{suffix}</span>
        )}
      </div>
      {helperText && <p className={baseHelperTextClassName}>{helperText}</p>}
    </div>
  );
};

export default Input;
